// Typographic wordmark for AeroChat.
// A clean, confident wordmark replaces the old SVG logo everywhere.
// We pair "aero" (regular weight) with "." (brand green) and "chat" (bold)
// to echo the product name while reading as a premium tech brand.

import React from 'react';
import { Box, Typography } from '@mui/material';
import { colors } from '../theme';

interface BrandProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  href?: string;
  subtitle?: string;
}

const SIZES = {
  sm: { font: '1.25rem', dot: '0.75rem', gap: 0 },
  md: { font: '1.75rem', dot: '1rem', gap: 0 },
  lg: { font: '3rem', dot: '1.75rem', gap: 0 },
  xl: { font: '4.5rem', dot: '2.5rem', gap: 0 },
};

export default function Brand({ size = 'md', href, subtitle }: BrandProps) {
  const s = SIZES[size];

  const content = (
    <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start' }}>
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'baseline',
          fontFamily: '"Inter", sans-serif',
          fontSize: s.font,
          fontWeight: 800,
          letterSpacing: '-0.04em',
          lineHeight: 1,
        }}
      >
        <Box component="span" sx={{ color: colors.text, fontWeight: 500 }}>
          aero
        </Box>
        <Box
          component="span"
          sx={{
            color: colors.brand,
            textShadow: `0 0 24px ${colors.brandShadow}`,
            transform: 'translateY(-0.05em)',
            display: 'inline-block',
            mx: '0.03em',
          }}
        >
          .
        </Box>
        <Box component="span" sx={{ color: colors.text, fontWeight: 800 }}>
          chat
        </Box>
      </Box>
      {subtitle && (
        <Typography
          sx={{
            mt: 0.75,
            fontSize: '0.625rem',
            fontWeight: 600,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: colors.textMuted,
            fontFamily: '"JetBrains Mono", monospace',
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );

  if (href) {
    return (
      <Box
        component="a"
        href={href}
        sx={{ textDecoration: 'none', color: 'inherit' }}
      >
        {content}
      </Box>
    );
  }
  return content;
}
